import {FC, useEffect, useMemo, useState} from 'react'
import {Card, Col, Image, Row, Spinner} from 'react-bootstrap'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import axios from '../../../libs/axios'
import {useAuth} from '../../../modules/auth'
import {getISO2ByISO3, getNameByISO3} from '../../../libs/misc'
import {getPreferentialTradePartners, runPreferenceLossSimulation} from './core/_requests'
import {filterAvailableGroups, hasNoPartners, noDataMessage} from './core/_transforms'
import {GroupDetail, PreferentialTradePartnersResponse, SimulationResponse} from './core/_models'
import AgreementSelectTable, {AgreementRow} from './components/AgreementSelectTable'
import SimulationSettings, {SettingsValue} from './components/SimulationSettings'
import PreferenceLossLoading from './components/PreferenceLossLoading'
import SummaryCard from './components/SummaryCard'
import ReporterAffectedTable from './components/ReporterAffectedTable'
import ReducedImportsTable from './components/ReducedImportsTable'
import IncreasedExportsTable from './components/IncreasedExportsTable'
import TwoDegreeDetailModal from './components/TwoDegreeDetailModal'
import AffectedTradeFlowsTable from './components/AffectedTradeFlowsTable'
import SimulationNotes from './components/SimulationNotes'

const PreferenceLossPage: FC = () => {
  const {currentUser} = useAuth()
  const reporter = currentUser?.economy || ''

  const [screen, setScreen] = useState<'form' | 'loading' | 'results'>('form')
  const [partners, setPartners] = useState<PreferentialTradePartnersResponse | null>(null)
  const [loadingPartners, setLoadingPartners] = useState(true)
  const [selectedBilateral, setSelectedBilateral] = useState<string[]>([])
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [settings, setSettings] = useState<SettingsValue>({
    year: 0,
    elasSupply: 999,
    elasSubstitution: 1.5,
    elasType: 1,
  })
  const [result, setResult] = useState<SimulationResponse | null>(null)
  const [flowsFilterEcon, setFlowsFilterEcon] = useState<string>('')
  const [modalEcon, setModalEcon] = useState<string>('')

  // group_details carried forward for tariff tooltip decoding (spec §5)
  const groupDetails: Record<string, GroupDetail> = partners?.group_details || {}

  useEffect(() => {
    if (!reporter) return
    setLoadingPartners(true)
    getPreferentialTradePartners(axios, reporter)
      .then((res) => {
        const data: PreferentialTradePartnersResponse = res.data
        setPartners(data)
        const years = [...(data.data_availability || [])].sort((a, b) => b - a).slice(0, 5)
        setSettings((prev) => ({...prev, year: years[0] || 0}))
      })
      .catch((e) => console.error(e))
      .finally(() => setLoadingPartners(false))
  }, [reporter])

  const bilateralRows: AgreementRow[] = useMemo(() => {
    if (!partners) return []
    return (partners.bilateral_partners || []).map((code) => ({
      code,
      label: code === 'EUN' ? 'European Union' : getNameByISO3(code),
      sublabel: `${partners.bilateral_details?.[code] ?? 0} commodities`,
      isEUN: code === 'EUN',
      iso2ForFlag: code === 'EUN' ? undefined : getISO2ByISO3(code),
    }))
  }, [partners])

  const groupRows: AgreementRow[] = useMemo(() => {
    if (!partners) return []
    return filterAvailableGroups(partners).map((code) => {
      const g = partners.group_details[code]
      const reps = (g.group_reporters || [])
        .map((r) => `${getNameByISO3(r.rep)} (${r.counter})`)
        .join(', ')
      return {code, label: g.title, sublabel: reps}
    })
  }, [partners])

  const noPartners = partners ? hasNoPartners(partners) : false
  const submitDisabled =
    loadingPartners || (selectedBilateral.length === 0 && selectedGroups.length === 0)

  const buildSimulationParams = () => ({
    econ: reporter,
    year: settings.year,
    elas_supply: settings.elasSupply,
    elas_substitution: settings.elasSubstitution,
    elas_type: settings.elasType,
    price_effect: 0 as const,
    exclude_bilateral_agreements: selectedBilateral,
    exclude_group_agreements: selectedGroups,
    combine_effects_diversion: true as const,
  })

  const handleAdjustParams = () => {
    setFlowsFilterEcon('')
    setModalEcon('')
    setScreen('form')
  }

  const handleSubmit = () => {
    if (!settings.year || isNaN(settings.year)) {
      Swal.fire({icon: 'error', title: 'Please select a valid import data year.'})
      return
    }
    if (settings.elasSupply < 0 || settings.elasSupply > 99999) {
      Swal.fire({icon: 'error', title: 'Supply elasticity must be between 0 and 99999.'})
      return
    }
    if (settings.elasSubstitution < 0 || settings.elasSubstitution > 10) {
      Swal.fire({icon: 'error', title: 'Substitution elasticity must be between 0 and 10.'})
      return
    }
    if (selectedBilateral.length === 0 && selectedGroups.length === 0) {
      Swal.fire({icon: 'error', title: 'Select at least one agreement to exclude.'})
      return
    }
    setResult(null)
    setScreen('loading')
    runPreferenceLossSimulation(axios, buildSimulationParams())
      .then((res) => {
        const data: SimulationResponse = res.data
        if (!data.meta || data.meta.counter_calc_trade_creation === 0) {
          setScreen('form')
          Swal.fire({icon: 'error', title: 'No results', html: noDataMessage(data.meta || {})})
          return
        }
        setResult(data)
        setScreen('results')
      })
      .catch((e) => {
        console.error(e)
        setScreen('form')
        Swal.fire({icon: 'error', title: 'Simulation failed', text: 'Please try again.'})
      })
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Preference Loss Simulation</PageTitle>

      <Row className='mb-5'>
        <Col>
          <Card className='card-custom card-border'>
            <Card.Body className='d-flex align-items-center'>
              <Image src='media/tina/img-result-documents.jpg' thumbnail width={110} />
              <div className='ms-5'>
                <h3 className='text-primary'>
                  Preference Loss Simulation
                  <span className='text-gray-700'>
                    <span className='fa-solid fa-angles-right px-1'></span>
                    impact on {getNameByISO3(reporter)}
                  </span>
                </h3>
                <div className='text-gray-700'>
                  Estimate the economic impact when {getNameByISO3(reporter)} loses preferential
                  tariff treatment. Select the bilateral and/or regional agreements to exclude, set
                  elasticity parameters, and run the partial-equilibrium simulation.
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {screen === 'form' && (
        <Row className='mb-5'>
          <Col lg={8}>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title>
                  <h3 className='text-gray-700'>Trade agreements to exclude</h3>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {loadingPartners ? (
                  <div className='d-flex align-items-center justify-content-center py-10'>
                    <Spinner animation='border' variant='primary' className='me-3' /> Loading
                    agreements…
                  </div>
                ) : noPartners ? (
                  <div className='alert alert-warning'>
                    No preferential trade partners are available for {getNameByISO3(reporter)}.
                  </div>
                ) : (
                  <>
                    <AgreementSelectTable
                      title='Bilateral agreements'
                      columnLabels={['Agreement with', 'Tariffs reported on']}
                      rows={bilateralRows}
                      selected={selectedBilateral}
                      onChange={setSelectedBilateral}
                      showEunNote={bilateralRows.some((r) => r.isEUN)}
                    />
                    <AgreementSelectTable
                      title='Regional / bloc agreements'
                      columnLabels={['Agreement', 'Tariffs reported by']}
                      rows={groupRows}
                      selected={selectedGroups}
                      onChange={setSelectedGroups}
                    />
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4}>
            <SimulationSettings
              availableYears={[...(partners?.data_availability || [])]
                .sort((a, b) => b - a)
                .slice(0, 5)}
              value={settings}
              onChange={setSettings}
              onSubmit={handleSubmit}
              submitDisabled={submitDisabled}
            />
          </Col>
        </Row>
      )}

      {screen === 'loading' && (
        <PreferenceLossLoading reporterName={getNameByISO3(reporter)} dataReady={false} />
      )}
      {screen === 'results' && result && (
        <div id='preference-loss-results'>
          {/* Tasks 10–14 render summary + tables here, passing result, reporter, groupDetails */}
          <Card className='mb-5'>
            <Card.Body>
              <SummaryCard
                result={result}
                onScrollToFlows={() =>
                  document
                    .getElementById('pref-loss-flows-table')
                    ?.scrollIntoView({behavior: 'smooth'})
                }
                onScrollToEconomies={() =>
                  document
                    .getElementById('pref-loss-economies-table')
                    ?.scrollIntoView({behavior: 'smooth'})
                }
              />
            </Card.Body>
          </Card>
          <ReporterAffectedTable result={result} />
          <ReducedImportsTable
            result={result}
            onBadgeClick={(econ) => {
              setFlowsFilterEcon(econ)
              document.getElementById('pref-loss-flows-table')?.scrollIntoView({behavior: 'smooth'})
            }}
          />
          <IncreasedExportsTable result={result} onBadgeClick={setModalEcon} />
          <TwoDegreeDetailModal
            show={!!modalEcon}
            econ={modalEcon}
            result={result}
            onHide={() => setModalEcon('')}
          />
          <AffectedTradeFlowsTable
            result={result}
            groupDetails={groupDetails}
            reporter={reporter}
            filterEcon={flowsFilterEcon}
          />
          <div className='mb-3'>
            <button
              className='btn btn-warning btn-sm fw-bold text-black'
              onClick={handleAdjustParams}
            >
              <i className='bi bi-sliders me-2 text-black'></i>Adjust Parameters or Run New Simulation
            </button>
          </div>
          <SimulationNotes
            result={result}
            reporterName={getNameByISO3(reporter)}
            onBackToForm={handleAdjustParams}
          />
        </div>
      )}
    </>
  )
}

export default PreferenceLossPage
